<template>
  <div class="performance-domain-blocker-chart">
    <div v-if="loading" class="flex justify-center items-center h-96">
      <div class="text-lg">Loading chart data...</div>
    </div>

    <div v-else-if="error" class="flex justify-center items-center h-96">
      <div class="text-red-500">{{ error }}</div>
    </div>

    <div v-else-if="chartData.values.length === 0" class="flex justify-center items-center h-96">
      <div class="text-gray-500">No performance blocker data available</div>
    </div>

    <Pie_Chart
      v-else
      :chart_data="chartData"
      :chart_config="chartConfig"
      :override-height="overrideHeight"
      @slice-click="handleSliceClick"
    />

    <div v-if="performance_blocker_major_category">
      <Performance_Domain_Blocker_Table
        :auto-height="true"
        :key="performance_blocker_major_category"
        :filters="{
          'performance_blocker_major_category': performance_blocker_major_category
        }"
      />
    </div>


  </div>
</template>

<script>
import axios from 'axios';
import Pie_Chart from "@/components/widgetcomponents/Viz_Elements/Pie_Chart.vue";
import MetricPerformanceBlockerTable from "@/components/Metric/Metric_Performance_Blocker_Table.vue";

export default {
  name: 'PerformanceDomainBlockerChart',
  components: {
    Performance_Domain_Blocker_Table: MetricPerformanceBlockerTable,
    Pie_Chart
  },
  props: {
    performanceDomainId: {
      type: Number,
      required: true
    },
    overrideHeight: {
      type: String,
      required: false
    },
    chartTitle: {
      type: String,
      required: false,
      default: 'Performance Blockers by Domain'
    },
    chartSubtitle: {
      type: String,
      required: false
    },
    showValues: {
      type: Boolean,
      default: true
    },
    showLabels: {
      type: Boolean,
      default: true
    },
    showLegend: {
      type: Boolean,
      default: false
    },
    legendPosition: {
      type: String,
      default: 'right',
      validator: value => ['right', 'bottom', 'none'].includes(value)
    },
    customColors: {
      type: Array,
      required: false
    }
  },
  emits: ['domain-click', 'data-loaded'],
  data() {
    return {
      loading: true,
      error: null,
      rawData: [],
      chartData: {
        values: [],
        labels: [],
        colors: null
      },
      performance_blocker_major_category: null
    };
  },
  computed: {
    chartConfig() {
      return {
        margin: { top: 80, right: 80, bottom: 80, left: 0 },
        innerRadius: 0,
        outerRadius: null,
        padAngle: 0.02,
        cornerRadius: 3,
        labelDisplay: {
          showLabels: this.showLabels,
          showValues: this.showValues,
          minAngleForLabel: 0.15,
          labelOffset: 20
        },
        legend: {
          display: false,
          position: this.legendPosition,
          spacing: 20
        },
        formatting: {
          percentage: true,
          sig_figs: 1
        },
        title: this.chartTitle,
        subtitle: this.chartSubtitle,
        tooltip: {
          enabled: true
        }
      };
    }
  },
  watch: {
    performanceDomainId() {
      this.performance_blocker_major_category = null;
      this.fetchData();
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get('/api/performanceblockers/summary/', {
          params: {
            performance_domain_id: this.performanceDomainId,
          }
        });

        if (response.data && response.data.length > 0) {
          this.processChartDataFromSummary(response.data);
          this.$emit('data-loaded', response.data);
        } else {
          this.chartData = {
            values: [],
            labels: [],
            colors: this.customColors || null
          };
        }
      } catch (error) {
        console.error('Error fetching performance blocker summary:', error);
        this.error = 'Failed to load performance blocker data. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    processChartDataFromSummary(summaryData) {
      const sortedData = summaryData.sort((a, b) => b.count - a.count);

      this.chartData = {
        labels: sortedData.map(item => item.performance_blocker_major_category),
        values: sortedData.map(item => item.count),
        colors: this.customColors || null
      };
    },
    handleSliceClick(sliceData) {
      // Set the filter for the table - the table component will fetch its own data
      this.performance_blocker_major_category = sliceData.label;
    },
    refreshData() {
      this.fetchData();
    },
    getDomainData(domainName) {
      return this.rawData.filter(
        item => (item.performance_blocker_type_name || 'Unknown Domain') === domainName
      );
    }
  }
};
</script>

<style scoped>
.performance-domain-blocker-chart {
  width: 100%;
}
</style>
